/**
 * A Help Center article (/help/:sectionId/:articleSlug): docs layout with the
 * section sidebar, the article and its byline, "On this page", feedback and
 * related articles.
 */
import { Link, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { HELP_CENTER } from '@/content/help';
import { articleMeta } from '@/content/help/seo';
import HelpShell from '@/components/help/HelpShell';
import HelpSeo from '@/components/help/HelpSeo';
import HelpSidebar from '@/components/help/HelpSidebar';
import AuthorByline from '@/components/help/AuthorByline';
import ArticleBody from '@/components/help/ArticleBody';
import OnThisPage from '@/components/help/OnThisPage';
import ArticleFeedback from '@/components/help/ArticleFeedback';
import { ContactPanel } from './HelpHome';
import HelpNotFound from './HelpNotFound';

const readingMinutes = (text) => Math.max(1, Math.round(String(text || '').split(/\s+/).filter(Boolean).length / 220));

export default function HelpArticlePage() {
  const { sectionId, articleSlug } = useParams();
  const article = HELP_CENTER.getArticle(sectionId, articleSlug);
  if (!article) return <HelpNotFound />;

  return (
    <HelpShell>
      <HelpSeo meta={articleMeta(article)} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 pb-24">
        <div className="grid gap-12 lg:grid-cols-[250px_minmax(0,1fr)] xl:grid-cols-[250px_minmax(0,1fr)_200px]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
              <HelpSidebar sectionId={article.sectionId} articleId={article.id} />
            </div>
          </aside>

          <article className="min-w-0 max-w-3xl">
            <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-[13px] text-white/50">
              <Link to="/help" className="hover:text-white">Help Center</Link>
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              <Link to={`/help/${article.sectionId}`} className="hover:text-white">{article.sectionTitle}</Link>
            </nav>
            <h1 className="mt-5 text-[30px] sm:text-[38px] leading-[1.15] font-semibold tracking-[-0.02em] text-white">{article.title}</h1>
            <div className="mt-6 pb-7 border-b border-white/[0.08]">
              <AuthorByline author={article.authorProfile} updated={article.updated} minutes={readingMinutes(article.plainText)} />
            </div>

            <div className="mt-4">
              <ArticleBody blocks={article.blocks} />
            </div>

            <div className="mt-12">
              <ArticleFeedback articleId={article.id} />
            </div>

            {article.relatedArticles.length > 0 && (
              <section aria-labelledby="related" className="mt-12">
                <h2 id="related" className="text-[18px] font-medium text-white">Related articles</h2>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {article.relatedArticles.map((r) => (
                    <li key={r.id}>
                      <Link to={r.path} className="block h-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5 hover:border-white/20 hover:bg-white/[0.05] transition-colors">
                        <span className="block text-[11.5px] uppercase tracking-[0.14em] text-white/45">{r.sectionTitle}</span>
                        <span className="mt-1 block text-[15px] font-medium text-white">{r.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="mt-12">
              <ContactPanel />
            </div>
          </article>

          <aside className="hidden xl:block">
            <div className="sticky top-24">
              <OnThisPage headings={article.headings} />
            </div>
          </aside>
        </div>
      </div>
    </HelpShell>
  );
}
